import { Fragment, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// @ts-expect-error module not typed
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { getRichText, getHasReplies, getPreviousRepliesHtml, getPreviousRepliesPlainText
// @ts-expect-error module not typed
} from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getOriginalFromSenderEmail } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { getClipStatus } from 'conversations-message-history/common-message-format/operators/emailMetadataGetters';
// @ts-expect-error module not typed
import { isEmailCMF } from 'conversations-message-history/common-message-format/operators/cmfComparators'; // @ts-expect-error module not typed

import { getInlineImagePendingIds } from 'conversations-message-history/common-message-format/operators/getInlineImagePendingIds'; // @ts-expect-error module not typed

import { getHasInlineImageAttachments } from 'conversations-message-history/common-message-format/operators/getHasInlineImageAttachments';
import { PREVIOUS_REPLIES_CLIPPED, MOST_RECENT_REPLY_CLIPPED, NOT_CLIPPED } from 'conversations-message-history/email-message/constants/messageClipStatus';
// @ts-expect-error module not typed
import EmailAttachmentDisabledBanner from './EmailAttachmentDisabledBanner';
// @ts-expect-error module not typed
import { getIsEmailAttachmentDisabled } from 'conversations-message-history/common-message-format/operators/getIsEmailAttachmentDisabled';
import MessageContent from './MessageContent';
import MessageReplies from './MessageReplies';
import MessageExpandButton from './MessageExpandButton';
import MessageClippedLink from './MessageClippedLink';
// @ts-expect-error module not typed
import InlineImageProcessingBanner from './InlineImageProcessingBanner';
import { getUsageTrackingChannel } from '../usage-tracker/trackerChannelIdMapping';
import { usageTracker } from '../usage-tracker/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CommonMessageContent = ({
  fetchReplies,
  handleFetchImageProcessingStatus,
  message,
  messageReplyIsLoading = false,
  numberOfProcessingImages,
  plainTextMode = false,
  shouldDisplayBanner = false,
  genericChannelId
}) => {
  const [showReplies, setShowReplies] = useState(false);
  const [showAttachmentDisabledBanner, setShowAttachmentDisabledBanner] = useState(false);
  const shouldDispatchFetchImageStatus = useRef(false);
  const [showInlineProcessingBanner, setShowInlineProcessingBanner] = useState(false);
  const showMessageReplies = showReplies && !messageReplyIsLoading;
  // should always show email signature if email has no replies
  const showTruncatedMessageContent = !getHasReplies(message) || showMessageReplies;
  const isEmailAttachmentDisabled = getIsEmailAttachmentDisabled(message);
  const originalSenderEmail = getOriginalFromSenderEmail(message);
  const shouldFetch = originalSenderEmail && getHasReplies(message);
  const messageClipStatus = isEmailCMF(message) && getClipStatus(message);
  const handleFetchReplies = useCallback(() => {
    const alreadyFetched = getPreviousRepliesHtml(message) || getPreviousRepliesPlainText(message);
    const notClipped = messageClipStatus === NOT_CLIPPED;
    if (!alreadyFetched && notClipped && fetchReplies) {
      fetchReplies(message);
    }
  }, [fetchReplies, message, messageClipStatus]);
  const handleExpandBtnClick = useCallback(() => {
    // this callback is only ever called if `hasReplies` === true
    // so we don't need to check for that when determining when to fetch
    handleFetchReplies();
    setShowReplies(!showReplies);
    usageTracker.track('view-email-chain', {
      action: 'view email chain',
      channel: getUsageTrackingChannel(genericChannelId)
    });
  }, [showReplies, handleFetchReplies, genericChannelId]);
  const shouldFetchImageStatus = getHasInlineImageAttachments(message) && getInlineImagePendingIds(message);
  const handleCloseAttachmentDisabledBanner = useCallback(() => {
    setShowAttachmentDisabledBanner(false);
  }, [setShowAttachmentDisabledBanner]);
  useEffect(() => {
    if (shouldFetch) {
      handleFetchReplies();
      setShowReplies(true);
    }
  }, [shouldFetch, setShowReplies, handleFetchReplies]);
  useEffect(() => {
    if (shouldFetchImageStatus && !shouldDispatchFetchImageStatus.current && handleFetchImageProcessingStatus) {
      handleFetchImageProcessingStatus();
      shouldDispatchFetchImageStatus.current = true;
    }
    setShowInlineProcessingBanner(shouldDisplayBanner);
  }, [shouldFetchImageStatus, shouldDispatchFetchImageStatus, handleFetchImageProcessingStatus, shouldDisplayBanner]);
  useEffect(() => {
    if (isEmailAttachmentDisabled) {
      setShowAttachmentDisabledBanner(true);
    }
  }, [isEmailAttachmentDisabled, setShowAttachmentDisabledBanner]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(MessageContent, {
      message: message,
      plainTextMode: plainTextMode,
      showTruncatedMessageContent: showTruncatedMessageContent
    }), messageClipStatus === MOST_RECENT_REPLY_CLIPPED && /*#__PURE__*/_jsx(MessageClippedLink, {
      message: message
    }), getHasReplies(message) && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(MessageExpandButton, {
        isMessageReplyLoading: messageReplyIsLoading,
        onClick: handleExpandBtnClick
      }), showMessageReplies && (messageClipStatus === PREVIOUS_REPLIES_CLIPPED ? /*#__PURE__*/_jsx(MessageClippedLink, {
        message: message
      }) : /*#__PURE__*/_jsx(MessageReplies, {
        isPlainText: plainTextMode || !getRichText(message),
        message: message
      }))]
    }), showInlineProcessingBanner && /*#__PURE__*/_jsx(InlineImageProcessingBanner, {
      imagesToProcess: numberOfProcessingImages,
      setShowInlineProcessingBanner: setShowInlineProcessingBanner
    }), showAttachmentDisabledBanner && /*#__PURE__*/_jsx(EmailAttachmentDisabledBanner, {
      onClose: handleCloseAttachmentDisabledBanner
    })]
  });
};
CommonMessageContent.displayName = 'CommonMessageContent';
CommonMessageContent.propTypes = {
  fetchReplies: PropTypes.func,
  handleFetchImageProcessingStatus: PropTypes.func.isRequired,
  message: PropTypes.oneOfType([RecordPropType('CommonMessage'), RecordPropType('WorkflowGeneratedUserMessage')]).isRequired,
  messageReplyIsLoading: PropTypes.bool,
  numberOfProcessingImages: PropTypes.number,
  plainTextMode: PropTypes.bool.isRequired,
  shouldDisplayBanner: PropTypes.bool
};
/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
CommonMessageContent.defaultProps = {
  handleFetchImageProcessingStatus: () => {}
};
export default CommonMessageContent;